import { Button } from '@/components/ui/Button/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/InputOtp/InputOtp';
import { toast } from '@/components/ui/Toast/Toast';
import { useCountdown } from '@/hooks/useCountdown';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPassword } from 'aws-amplify/auth';
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const RESEND_COOLDOWN_IN_SECONDS = 60;

interface AuthCodeFormProps {
  goToNextStep: () => void;
  username: string;
  setCode: Dispatch<SetStateAction<string>>;
  errorMessage?: string;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const AuthCodeForm = ({ goToNextStep, username, setCode, errorMessage, setIsLoading }: AuthCodeFormProps) => {
  const { t } = useTranslation('auth', { keyPrefix: 'forgotPassword.steps.code' });
  const { t: tValidation } = useTranslation('utils', { keyPrefix: 'validation' });
  const { t: tError } = useTranslation('utils', { keyPrefix: 'error' });
  const { translateError } = useErrorTranslation();

  const [isResending, setIsResending] = useState(false);
  const [ariaAnnouncement, setAriaAnnouncement] = useState('');
  const previousCountdown = useRef(0);

  const { countdown: resendCooldown, startCountdown: startResendCooldown } = useCountdown();

  const formSchema = z.object({
    code: z
      .string()
      .min(1, { message: tValidation('required') })
      .length(6, { message: tValidation('wrongFormat', { field: t('code.shortLabel') }) }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (errorMessage) {
      form.setError('code', { message: errorMessage });
    }
  }, [errorMessage, form]);

  // Handle aria announcements for countdown
  useEffect(() => {
    const previousValue = previousCountdown.current;

    switch (resendCooldown) {
      case RESEND_COOLDOWN_IN_SECONDS: {
        // Countdown just started
        setAriaAnnouncement(t('ariaAnnouncements.countdownStarted', { seconds: RESEND_COOLDOWN_IN_SECONDS }));
        break;
      }
      case 30: {
        // 30 seconds left
        setAriaAnnouncement(t('ariaAnnouncements.secondsLeft', { seconds: 30 }));
        break;
      }
      case 10: {
        // 10 seconds left
        setAriaAnnouncement(t('ariaAnnouncements.secondsLeft', { seconds: 10 }));
        break;
      }
      case 0: {
        // Resend is now available - only announce if countdown was running (previous value > 0)
        if (previousValue > 0) {
          setAriaAnnouncement(t('ariaAnnouncements.resendAvailable'));
        }
        break;
      }
      default: {
        // Clear announcement
        setAriaAnnouncement('');
        break;
      }
    }

    // Update the previous value for next render
    previousCountdown.current = resendCooldown;
  }, [resendCooldown, t]);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      setCode(values.code);
      goToNextStep();
    } catch (error) {
      toast({
        variant: 'destructive',
        description: translateError(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);

    try {
      await resetPassword({ username: username.toLowerCase() });
      toast({
        description: t('resendSuccess'),
      });
      startResendCooldown(RESEND_COOLDOWN_IN_SECONDS);
    } catch (error) {
      toast({
        variant: 'destructive',
        description: translateError(error, t(`cognitoErrors.${(error as Error)?.name}` as never, tError('GENERAL_ERROR'))),
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Form {...form}>
      {/* Aria live region for countdown announcements */}
      <div aria-live='polite' aria-atomic='true' className='sr-only'>
        {ariaAnnouncement}
      </div>

      <form
        id='forgot-password-code-form'
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className='flex w-full flex-col items-center justify-center gap-4'
      >
        <FormField
          control={form.control}
          name='code'
          render={({ field }) => (
            <div className='flex flex-col items-center justify-center pt-1'>
              <FormItem className='flex flex-col items-center gap-2'>
                <FormLabel className='sr-only'>{t('code.longLabel')}</FormLabel>
                <FormControl>
                  <InputOTP maxLength={6} value={field.value} onChange={field.onChange} isError={!!form.formState.errors.code} className='w-full'>
                    <InputOTPGroup>
                      {Array.from({ length: 6 }, (_, i) => (
                        <InputOTPSlot key={i} index={i} />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>
            </div>
          )}
        />
      </form>
      <div className='mt-6 flex w-full items-center justify-center gap-2'>
        {resendCooldown > 0 ? (
          <span className='text-body-m-regular text-text-default-secondary'>{t('resendCooldown', { cooldown: resendCooldown })}</span>
        ) : (
          <>
            <span className='text-body-m-regular'>{t('noCode')}</span>
            <Button variant='link' className='text-link-m-bold px-2' onClick={() => void resendCode()} isLoading={isResending} disabled={isResending}>
              {t('resend')}
            </Button>
          </>
        )}
      </div>
    </Form>
  );
};
